import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { useOnboardingState } from '@/hooks/useConfig'
import { FinishOnWebsite } from './FinishOnWebsite'

// Brian: onboarding happens on the website; the iPhone app never shows sign up
// or setup.
const isNative = Capacitor.isNativePlatform()

/**
 * Forces setup before the rest of the app is reachable.
 *
 * An unconfigured client has no regions and no topics, so the headline pull
 * returns nothing and the deck looks broken rather than empty. Sending them to
 * setup first is the difference between "new product" and "product does not
 * work".
 */
export function OnboardingGuard({ children }: { children: ReactNode }) {
  const { data, isLoading } = useOnboardingState()
  const location = useLocation()

  // Never redirect on an unknown state: a flash to setup for someone who has
  // already onboarded is worse than a moment of nothing.
  if (isLoading || !data) return <div className="feed-loading">Loading...</div>

  if (!data.onboarded && isNative) return <FinishOnWebsite />

  if (!data.onboarded && location.pathname !== '/app/onboarding') {
    return <Navigate to="/app/onboarding" replace />
  }

  // Equally, do not strand someone on setup once it is finished.
  if (data.onboarded && location.pathname === '/app/onboarding') {
    return <Navigate to="/app" replace />
  }

  return <>{children}</>
}
